/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/bolmateSelect.scss';

import React, {
    CSSProperties,
    ForwardedRef,
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';

import {AwesomeIcon, ErrorType, hasHover, remToPx} from '@dvrd/dvr-controls';

import classNames from 'classnames';

import {COLOR_BLUE_BOLMATE} from '../../../util/constants';

import {VATValue} from '../../../util/interfaces';

import {translateVatValue, VATValuesMatch} from '../../../util/vatUtil';

interface Props {
    onChange: (value: VATValue) => void;
    onClick?: () => void;
    canOpen?: () => boolean;
    value: VATValue;
    defaultValue?: VATValue | null;
    items: Array<VATValue>;
    disabled?: boolean;
    error?: ErrorType;
    label?: string;
    className?: string;
    labelClassName?: string;
    valueClassName?: string;
    arrowClassName?: string;
    errorClassName?: string;
    itemContainerClassName?: string;
    itemClassName?: string;
    itemsPosition?: 'top' | 'bottom';
    itemsHeight?: number | string;
    initialOpen?: boolean;
    highlightSelected?: boolean;
    dynamicWidth?: boolean;
    active?: boolean;
    notSetAsText?: boolean;
}

export interface BolmateSelectRef {
    isOpen: () => boolean;
    onOpen: () => void;
}

function BolmateSelect(props: Props, ref: ForwardedRef<BolmateSelectRef>) {
    const {
        initialOpen,
        disabled,
        canOpen,
        onClick,
        onChange,
        value,
        items,
        active,
        label,
        labelClassName,
        valueClassName,
        arrowClassName,
        itemsHeight,
        itemsPosition,
        itemContainerClassName,
        itemClassName,
        highlightSelected,
        error,
        errorClassName,
        dynamicWidth,
        className,
        defaultValue,
        notSetAsText
    } = props;
    const [open, setOpen] = useState(initialOpen ?? false);
    const container = useRef<HTMLDivElement>(null);
    const contentStyle = useMemo(() => {
        const style: CSSProperties = {};
        if (active) style.border = `2px solid ${COLOR_BLUE_BOLMATE}`;
        return style;
    }, [active]);

    function onClickElement() {
        if (disabled || !(canOpen?.() ?? true)) return;
        setOpen(!open);
        onClick?.();
    }

    function onClickItem(value: VATValue) {
        return function (evt: React.MouseEvent) {
            evt.stopPropagation();
            onChange(value);
            setOpen(false);
        };
    }

    function onOpen() {
        setOpen(true);
    }

    function isOpen(): boolean {
        return open;
    }

    function renderLabel() {
        if (!label) return null;
        return (
            <label className={classNames('bolmate-select-label', labelClassName)} onClick={onClickElement}>
                {label}
            </label>
        );
    }

    function renderValue() {
        return (
            <label className={classNames('bolmate-select-value', valueClassName)} onClick={onClickElement}>
                {translateVatValue(value, defaultValue)}
            </label>
        );
    }

    function renderArrow() {
        return (
            <AwesomeIcon
                name='chevron-down'
                onClick={onClickElement}
                className={classNames('bolmate-select-arrow', open && 'open', arrowClassName)}
            />
        );
    }

    function renderItems() {
        let style: CSSProperties | undefined = undefined;
        if (itemsHeight !== undefined) {
            let height: string;
            if (typeof itemsHeight === 'number') height = itemsHeight + 'px';
            else height = itemsHeight;
            style = {maxHeight: height, overflowY: 'auto'};
        }
        return (
            <div
                style={style}
                className={classNames(
                    'bolmate-select-items',
                    open && !disabled && 'open',
                    itemsPosition ?? 'bottom',
                    itemContainerClassName
                )}
            >
                {items.map(renderItem)}
            </div>
        );
    }

    function renderItem(item: VATValue, index: number) {
        const isSelected: boolean = VATValuesMatch(item, value);
        return (
            <label
                key={index}
                className={classNames(
                    'bolmate-select-item',
                    itemClassName,
                    isSelected && highlightSelected && 'selected'
                )}
                onClick={onClickItem(item)}
            >
                {translateVatValue(item, defaultValue, {notSetAsText})}
            </label>
        );
    }

    function renderError() {
        if (!error) return null;
        return <label className={classNames('bolmate-select-error', errorClassName)}>{error}</label>;
    }

    function clickListener() {
        if (!hasHover(container.current)) setOpen(false);
    }

    function setWidth() {
        if (!dynamicWidth) return;
        const _container = container.current;
        const itemsContainer = _container?.querySelector('div.bolmate-select-items'),
            contentContainer = _container?.querySelector('div.content-container');
        if (itemsContainer && contentContainer) {
            (contentContainer as HTMLElement).style.width =
                itemsContainer.getBoundingClientRect().width + remToPx(1.75) + 'px';
        }
    }

    useImperativeHandle(ref, () => ({onOpen, isOpen}));

    useEffect(() => {
        window.setTimeout(() => {
            if (open) document.addEventListener('click', clickListener);
            else document.removeEventListener('click', clickListener);
        });
    }, [open]);

    useEffect(() => {
        window.setTimeout(setWidth);
    }, [items, dynamicWidth]);

    return (
        <div
            className={classNames(
                'bolmate-select-container',
                disabled && 'disabled',
                open && 'open',
                !!error && 'error',
                dynamicWidth && 'dynamic',
                className
            )}
            ref={container}
            onClick={onClickElement}
        >
            {renderLabel()}
            <div className='content-container' style={contentStyle}>
                {renderValue()}
                {renderArrow()}
            </div>
            {renderItems()}
            {renderError()}
        </div>
    );
}

export default forwardRef<BolmateSelectRef, Props>(BolmateSelect);
